import { Form, message, SelectProps, Spin } from "antd";
import { FC, useEffect } from "react";
import {
  Category,
  CategoryStatus,
  CategoryType,
} from "../../../dto/Category.dto";
import { CreateCategory } from "../../../dto/request/categories-request.dto";
import {
  useCreateCategory,
  useUpdateCategory,
} from "../../../services/api/category";
import Button from "../Controls/Button";
import Select from "../Controls/Select";
import TextInput from "../Controls/TextInput";
import PositionInput from "../Controls/PositionInput";

interface OnChangeHandler {
  (e: any): void;
}

interface CreateWatchFaceFormProps {
  onSubmit?: OnChangeHandler;
  onFinish?: OnChangeHandler;
  onChange?: OnChangeHandler;
  onCancel?: OnChangeHandler;
  onOk?: OnChangeHandler;
  category?: Category;
}

export type FieldType = Partial<CreateCategory>;

const categoryTypeOption: SelectProps["options"] = [
  { label: "Admin", value: CategoryType.Admin },
  { label: "Customer", value: CategoryType.Customer },
];

const categoryStatusOption: SelectProps["options"] = [
  { label: "Published", value: CategoryStatus.PUBLISHED },
  { label: "Hidden", value: CategoryStatus.HIDDEN },
];

const CreateCategoryForm: FC<any> = ({
  onSubmit,
  onFinish,
  onChange,
  onCancel,
  onOk,
  category,
  ...args
}: CreateWatchFaceFormProps) => {
  const [form] = Form.useForm();
  const categoryType = Form.useWatch("type", form);

  const { mutate: createCategory, isLoading: isCreating } = useCreateCategory({
    onSuccess: (data) => {
      form.resetFields();
      onOk?.(data);
    },
    onError: (err) => {
      const errorMessage =
        err?.response?.data?.error?.message || "Something went wrong";
      const errorArray = err?.response?.data?.error?.errors;
      if (Array.isArray(errorArray)) {
        errorArray.forEach((element) => {
          const errors = element.constraints;
          message.error(Object.values(errors)[0]?.toString());
        });
      } else {
        message.error(errorMessage);
      }
    },
  });

  const { mutate: updateCategory, isLoading: isUpdating } = useUpdateCategory({
    onSuccess: (data) => {
      form.resetFields();
      onOk?.(data);
    },
    onError: (err) => {
      const errorMessage =
        err?.response?.data?.error?.message || "Something went wrong";
      const errorArray = err?.response?.data?.error?.errors;
      if (Array.isArray(errorArray)) {
        errorArray.forEach((element) => {
          const errors = element.constraints;
          message.error(Object.values(errors)[0]?.toString());
        });
      } else {
        message.error(errorMessage);
      }
    },
  });

  const handleOnFinish = async (values: any): Promise<void> => {
    const body = {
      ...values,
      position: +values.position,
    };
    if (category?.id) {
      updateCategory({ id: category.id, data: body });
    } else {
      createCategory(body);
    }
  };

  const handleOnChange = (e: any): void => {
    onChange?.(e);
  };

  const handleOnCancel = (e: any): void => {
    form.resetFields();
    onCancel?.(e);
  };

  useEffect(() => {
    if (category === null) {
      form.resetFields();

      return;
    }

    if (!category) {
      return;
    }

    const { position, ...categoryWithoutPosition } = category;
    form.setFieldsValue({
      ...categoryWithoutPosition,
    });
  }, [form, category]);

  const isLoading = isCreating || isUpdating;

  return (
    <Spin spinning={isLoading} tip="Saving...">
      <Form
        onChange={handleOnChange}
        onFinish={handleOnFinish}
        form={form}
        initialValues={category}
        autoComplete="off"
        layout="vertical"
        {...args}
      >
      <Form.Item<FieldType>
        name={"name"}
        label="Name"
        rules={[{ required: true, message: "Please input category name!" }]}
      >
        <TextInput placeholder="Category name" />
      </Form.Item>

      <Form.Item<FieldType>
        name={"type"}
        label="Type"
        rules={[{ required: true, message: "Please select category type!" }]}
      >
        <Select
          style={{ width: "100%" }}
          placeholder="Category type"
          options={categoryTypeOption}
        />
      </Form.Item>

      <Form.Item<FieldType>
        name={"status"}
        label="Status"
        rules={[{ required: true, message: "Please select category status!" }]}
      >
        <Select
          style={{ width: "100%" }}
          placeholder="Category status"
          options={categoryStatusOption}
        />
      </Form.Item>

      <Form.Item<FieldType> name={"position"} label="Position">
        <PositionInput
          endpoint="/admin/category"
          queryParamName="type"
          queryParamValue={categoryType}
          entityId={category?.id}
        />
      </Form.Item>

      <Button htmlType="submit" loading={isLoading}>
        {category?.id ? "Update" : "Create"}
      </Button>
      <Button onClick={handleOnCancel} disabled={isLoading}>
        Cancel
      </Button>
    </Form>
    </Spin>
  );
};

export default CreateCategoryForm;
